/**
 * AddOutfitToCalendarModal — Sélection d'une tenue à attribuer à une date.
 *
 * Sources :
 *   • Tenue du jour       → look_id + items + source='tenue_du_jour'
 *   • Mes Looks (perso)   → look_id + items + source='mes_looks'
 *   • Manuel              → photo de l'utilisateur + occasion + notes (source='manual')
 *
 * Lié au CalendarScreen.handleDayPress : remplace le placeholder précédent.
 *
 * v1.12.225
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator,
  Image, Alert, TextInput, useWindowDimensions,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AppModal from './AppModal';
import { showAppAlert } from './AppDialog';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import { addEntryWithOptionalPhoto, CALENDAR_OCCASIONS } from '../services/CalendarService';
import { addOutfitToCalendar, saveCalendarSnapshot } from '../services/CalendarAgentService';
import { getResultsForOutfit } from '../services/TryOnResultsService';
import LazyImage from './LazyImage';

const GOLD = '#D4AF72';
const BORDER = '#EAE6E0';

// v1.12.812 — normalise URI : force https, strip data:, retourne null si invalide
function safeImgUri(uri) {
  if (!uri || typeof uri !== 'string') return null;
  if (uri.startsWith('data:')) return null;
  // Force https sur http://
  if (uri.startsWith('http://')) return 'https://' + uri.slice(7);
  if (uri.startsWith('https://')) return uri;
  return null;
}

function pad2(n) { return String(n).padStart(2, '0'); }
function dateToKey(d) { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }

export default function AddOutfitToCalendarModal({
  visible, onClose, onAdded,
  date,                   // Date object — jour ciblé (mode "depuis grille calendrier")
  homeOutfit,             // tenue du jour courante (objet { items, id, name? })
  homeOutfitOptions = [], // v1.12.243 — top 5 alternatives pour Régénérer
  myLooks = [],           // userCreatedLooks
  looksIA = [],           // v1.12.238 — outfits Looks IA (cache LooksScreen)
  wardrobe = [],          // v1.12.246 — dressing user pour filtre par article
  // v1.12.250 — Mode "raccourci calendrier" : un look est pré-sélectionné depuis Home/Looks/Mes Looks
  // → la modal s'ouvre directement sur le date picker au lieu du choix de source
  preselectedLook = null,
  preselectedSource = null, // 'tenue_du_jour' | 'mes_looks' | 'looks_ia'
}) {
  const { user, getValidToken } = useAuth();
  const { t, lang } = useI18n();
  // v1.12.542 — Auto-charge les sources si vides (utilisé quand le modal est ouvert
  // depuis LooksScreen/OutfitCard sans passer les listes complètes, et que l'user
  // bascule à step='source' après cancel d'un conflit)
  const [internalLooksIA, setInternalLooksIA] = useState(looksIA);
  const [internalMyLooks, setInternalMyLooks] = useState(myLooks);
  useEffect(() => { setInternalLooksIA(looksIA); }, [looksIA]);
  useEffect(() => { setInternalMyLooks(myLooks); }, [myLooks]);
  // v1.13.18 — GA : ouverture du modal calendrier
  useEffect(() => { if (visible) { try { require('../services/AnalyticsService').trackTap('ui_calendar_open'); } catch {} } }, [visible]);
  useEffect(() => {
    if (!visible) return;
    // Charge looksIA depuis AsyncStorage si vide, sinon fallback API
    if ((!looksIA || looksIA.length === 0) && user?.id) {
      try {
        const { getLooksIASnapshot } = require('../services/CalendarAgentService');
        getLooksIASnapshot({ userId: user.id }).then(async list => {
          if (Array.isArray(list) && list.length > 0) {
            console.log(`[CalendarModal] loaded ${list.length} AI looks from AsyncStorage`);
            setInternalLooksIA(list);
            return;
          }
          // v1.12.810 — Fallback API : si AsyncStorage vide, fetch direct /looks?type=ai
          console.log('[CalendarModal] AsyncStorage empty, fetching AI looks from API...');
          try {
            const { fetchLooksAI } = require('../services/LooksApiService');
            const res = await fetchLooksAI({ limit: 60 });
            const apiLooks = Array.isArray(res?.looks) ? res.looks : [];
            if (apiLooks.length > 0) {
              // Adapt API looks to the format the modal expects
              const adapted = apiLooks.map((l, idx) => ({
                id: l.lookId || l.id || `api_ai_${idx}`,
                name: l.name || (l.bestTag ? `Look ${l.bestTag}` : 'Look IA'),
                occasion: l.bestTag || l.occasion || null,
                score: l.score || null,
                items: (l.items || []).slice(0, 6).map(it => ({
                  id: it.product_id || it.id || `${idx}_${it.category || 'x'}`,
                  name: it.name || '',
                  brand: it.brand || '',
                  image_url: it.photoUrl || it.image_url || it.image || null,
                  category: it.category || null,
                  pseudo: it.ai_metadata?.pseudo || it.pseudo || null,
                })),
              }));
              console.log(`[CalendarModal] fetched ${adapted.length} AI looks from API`);
              setInternalLooksIA(adapted);
            } else {
              console.warn(`[CalendarModal] API returned ${apiLooks.length} AI looks (computing=${res?.computing})`);
            }
          } catch (apiErr) {
            console.warn('[CalendarModal] fetchLooksAI failed:', apiErr?.message);
          }
        }).catch(() => {});
      } catch {}
    }
    // Charge myLooks via API si vide
    if ((!myLooks || myLooks.length === 0) && user) {
      try {
        const { listLooks } = require('../services/LookService');
        listLooks({ user, getValidToken }).then(list => {
          if (Array.isArray(list) && list.length > 0) setInternalMyLooks(list);
        }).catch(() => {});
      } catch {}
    }
  }, [visible, user, getValidToken, looksIA, myLooks]);
  // Override les variables locales utilisées dans le rendu
  looksIA = internalLooksIA;
  myLooks = internalMyLooks;
  const insets = useSafeAreaInsets();
  const { height: winH } = useWindowDimensions();
  // v1.12.236 — la card prend max 90% du viewport, footer CTA en bas hors-scroll
  const cardMaxHeight = Math.min(720, winH * 0.92);
  const headerH = 70;
  const footerH = 80;
  const scrollMaxHeight = Math.max(220, cardMaxHeight - headerH - footerH);
  // v1.12.250 — date_picker = nouvelle étape pour choisir la date depuis un raccourci
  const [step, setStep] = useState(preselectedLook ? 'date_picker' : 'source');
  // v1.12.250 — date courante en mode raccourci (l'user la choisit ; default = aujourd'hui)
  const [pickedDate, setPickedDate] = useState(() => date || new Date());
  // v1.12.288 — mois affiché dans le mini-calendrier (par défaut mois de la date sélectionnée)
  const [calendarViewYM, setCalendarViewYM] = useState(null);
  const [selectedLook, setSelectedLook] = useState(preselectedLook);
  // Si props changent (re-open), re-init
  React.useEffect(() => {
    if (visible && preselectedLook) {
      setSelectedLook(preselectedLook);
      setStep('date_picker');
      setPickedDate(date || new Date());
    } else if (visible && !preselectedLook) {
      setStep('source');
      setPickedDate(date || new Date());
    }
  }, [visible, preselectedLook?.id, date?.toString?.()]);
  const [manualPhoto, setManualPhoto] = useState(null);
  const [occasion, setOccasion] = useState(null);
  const [notes, setNotes] = useState('');
  // v1.12.740 — Choix de la photo principale pour le calendrier
  // null/'articles' = grille des items (defaut backend), 'flatlay' = image flat-lay, 'tryon' = image try-on
  const [lookResults, setLookResults] = useState(null);
  const [photoSource, setPhotoSource] = useState('articles');
  useEffect(() => {
    if (!selectedLook || !visible) return;
    setLookResults(null);
    setPhotoSource('articles');
    getResultsForOutfit(selectedLook).then((r) => {
      if (r && (r.flatlay || r.tryon)) {
        setLookResults(r);
        // Default = la dernière mise à jour si l'user en a déjà choisi une
        if (r.latest === 'flatlay' || r.latest === 'tryon') {
          setPhotoSource(r.latest);
        }
      }
    }).catch(() => {});
  }, [selectedLook, visible]);
  // v1.12.242 — Recherche + filtre catégorie pour Looks IA
  const [iaSearch, setIaSearch] = useState('');
  const [iaCatFilter, setIaCatFilter] = useState('Tout');
  // v1.12.243 — Index pour bouton Régénérer Tenue du jour (cycle dans homeOutfitOptions)
  const [tjIndex, setTjIndex] = useState(0);
  // v1.12.246 — Filtre Looks IA par articles (sélection via thumbs wardrobe)
  const [iaArticleIds, setIaArticleIds] = useState(() => new Set());
  // v1.12.811 — Filtre par article ouvert par défaut (recherche visuelle prominente)
  const [iaShowArticleFilter, setIaShowArticleFilter] = useState(true);
  // v1.12.249 — Pagination Looks IA (par batchs de 150) si plus de 150 looks
  const PAGE_SIZE = 150;
  const [iaVisibleCount, setIaVisibleCount] = useState(PAGE_SIZE);

  // v1.12.740 — FIX [[appmodal-callback-stable]] :
  // onRequestClose DOIT être une ref stable, sinon AppModal sur web déclenche
  // un cycle history.back qui ferme le modal dès qu'un re-render se produit
  // (ici : provoqué par le useEffect qui fetch lookResults asynchrone).
  const reset = useCallback(() => {
    setStep('source');
    setSelectedLook(null);
    setManualPhoto(null);
    setOccasion(null);
    setNotes('');
    setIaSearch('');
    setIaCatFilter('Tout');
    setIaArticleIds(new Set());
    setIaShowArticleFilter(false);
    setIaVisibleCount(PAGE_SIZE);
  }, []);

  const handleClose = useCallback(() => { reset(); onClose && onClose(); }, [reset, onClose]);

  // v1.12.243 — Onglets par STYLE/OCCASION du look (Casual / Chic / Soirée / etc.)
  // au lieu des catégories d'items (Robes / Hauts / Bas). Capitalise pour affichage.
  const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;
  const iaAvailableCats = useMemo(() => {
    const set = new Set();
    for (const l of (looksIA || [])) {
      const tag = (l.occasion || '').toString().trim();
      if (tag) set.add(cap(tag));
    }
    // Ordre préféré (premiers en tête)
    const ORDER = ['Casual', 'Chic', 'Bureau', 'Soirée', 'Sport', 'Romantique', 'Streetwear', 'Minimaliste', 'Workwear', 'Bohème'];
    const present = ORDER.filter(c => set.has(c));
    const others = Array.from(set).filter(c => !ORDER.includes(c));
    return ['Tout', ...present, ...others];
  }, [looksIA]);

  const filteredLooksIA = useMemo(() => {
    const q = (iaSearch || '').toLowerCase().trim();
    return (looksIA || []).filter(l => {
      // Filtre style/occasion
      if (iaCatFilter !== 'Tout') {
        const tag = cap((l.occasion || '').toString().trim());
        if (tag !== iaCatFilter) return false;
      }
      // v1.12.246 — Filtre par articles sélectionnés (le look doit contenir AU MOINS 1)
      if (iaArticleIds.size > 0) {
        const lookItemIds = new Set((l.items || []).map(it => String(it.id)));
        const lookItemNames = new Set((l.items || []).map(it => (it.name || '').toLowerCase().trim()).filter(Boolean));
        const lookItemPseudos = new Set((l.items || []).map(it => (it.pseudo || '').toLowerCase().trim()).filter(Boolean));
        let matchesAny = false;
        for (const w of (wardrobe || [])) {
          if (!iaArticleIds.has(String(w.id))) continue;
          if (lookItemIds.has(String(w.id))) { matchesAny = true; break; }
          const wn = (w.name || '').toLowerCase().trim();
          if (wn && lookItemNames.has(wn)) { matchesAny = true; break; }
          const wp = (w.ai_metadata?.pseudo || '').toLowerCase().trim();
          if (wp && lookItemPseudos.has(wp)) { matchesAny = true; break; }
        }
        if (!matchesAny) return false;
      }
      // Recherche texte (nom / occasion / nom items / marque / pseudo)
      if (!q) return true;
      const hay = [
        l.name, l.occasion,
        ...(l.items || []).flatMap(it => [it.name, it.brand, it.category, it.pseudo]),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [looksIA, iaSearch, iaCatFilter, iaArticleIds, wardrobe]);

  // v1.12.250 — utilise pickedDate si raccourci, sinon prop date
  const effectiveDate = preselectedLook ? pickedDate : date;
  const dateStr = useMemo(() => effectiveDate ? dateToKey(effectiveDate) : null, [effectiveDate]);
  const dateLabel = useMemo(() => {
    if (!effectiveDate) return '';
    const locale = lang === 'en' ? 'en-US' : 'fr-FR';
    return effectiveDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' });
  }, [effectiveDate, lang]);

  async function pickPhoto() {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert(t('permission_required'), t('permission_photos_required'));
        return;
      }
      const r = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.85,
      });
      if (!r.canceled && r.assets?.[0]?.uri) {
        setManualPhoto(r.assets[0].uri);
      }
    } catch (e) {
      Alert.alert(t('error'), e.message || t('photo_selection_failed'));
    }
  }

  async function submit({ source, look, photoUri, replace = false }) {
    if (!user || !dateStr) return;
    try { require('../services/AnalyticsService').trackTap('ui_calendar_save_outfit', { source: source || 'unknown', replace: !!replace }); } catch {}
    setStep('submitting');
    try {
      const token = getValidToken ? await getValidToken() : user.accessToken;
      let items = (look?.items || []).slice(0, 8).map((it, idx) => ({
        item_type: 'dressing_item',
        item_id: String(it.id || it.name || `i_${idx}`),
        category: it.category || null,
        position_order: idx,
      }));
      // v1.12.241 — Source manuelle (photo seule) : le backend exige ≥1 item.
      // On envoie un sentinel pour respecter la contrainte sans imposer
      // d'article réel à l'utilisateur.
      if (items.length === 0 && source === 'manual') {
        items = [{
          item_type: 'dressing_item',
          item_id: '__manual_photo__',
          category: null,
          position_order: 0,
        }];
      }
      // v1.12.234 — look_id en DB est UUID. Les outfits "tenue du jour" / "mes_looks"
      // ont des IDs JS (outfit_v2_xxx, engine_xxx, look_<timestamp>) → on n'envoie
      // look_id que si c'est un vrai UUID, sinon null. Le `source` suffit pour traçabilité.
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const lookIdToSend = (look?.id && UUID_RE.test(String(look.id))) ? look.id : null;
      // v1.12.233 — passe par CalendarAgentService.addOutfitToCalendar (check conflit + insights)
      const result = await addOutfitToCalendar({
        payload: {
          date: dateStr,
          source,
          look_id: lookIdToSend,
          occasion: occasion || look?.occasion || null,
          notes: (notes || '').trim() || null,
          items,
        },
        photoUri: photoUri || null,
        authToken: token,
        replace,
        user,
      });
      // Conflit détecté : proposer de remplacer
      // v1.12.289 — fix infinite loader sur web : showAppAlert au lieu d'Alert.alert
      if (!result.success && result.errors?.[0]?.code === 'DATE_CONFLICT') {
        const existing = result.errors[0].existing_entry;
        const fallbackName = lang === 'en' ? 'saved' : 'enregistrée';
        const piecesLabel = lang === 'en' ? 'items' : 'pièces';
        const conflictMsg = lang === 'en'
          ? `${dateLabel} already has the outfit "${existing?.name || fallbackName}" (${existing?.items_count || 0} ${piecesLabel}).\n\nReplace with your new selection?`
          : `Le ${dateLabel} a déjà la tenue "${existing?.name || fallbackName}" (${existing?.items_count || 0} ${piecesLabel}).\n\nRemplacer par votre nouvelle sélection ?`;
        const idx = await showAppAlert(
          t('outfit_already_exists'),
          conflictMsg,
          [
            { text: t('cancel'), style: 'cancel' },
            { text: t('replace'), style: 'destructive' },
          ],
        );
        if (idx === 1) {
          // recall submit with replace=true (le state passe à submitting puis retombera)
          return submit({ source, look, photoUri, replace: true });
        }
        setStep('source');
        return;
      }
      if (!result.success) {
        await showAppAlert(t('error'), result.errors?.[0]?.message || t('cannot_save_outfit'));
        setStep('source');
        return;
      }
      try { require('../services/AnalyticsService').trackEvent('calendar_outfit_saved', { source: source || 'unknown' }); } catch {}
      // v1.12.238 — sauvegarder snapshot local pour affichage détaillé ultérieur
      try {
        await saveCalendarSnapshot({
          userId: user?.id,
          date: dateStr,
          look: look || null,
          source,
          photo: photoUri || null,
        });
      } catch {}
      onAdded && onAdded();
      handleClose();
    } catch (e) {
      Alert.alert(t('error'), e?.message || t('cannot_save_outfit'));
      setStep('source');
    }
  }

  if (!visible) return null;

  return (
    <AppModal visible transparent animationType="fade" onRequestClose={handleClose} webBackdropClosable webMaxWidth={520}>
      {/* v1.12.229 — marge top = safe area inset pour ne pas toucher la barre système */}
      <View style={[s.card, { marginTop: Math.max(insets.top, 24), maxHeight: cardMaxHeight }]}>
        <View style={s.header}>
          <Text style={s.title}>{t('add_outfit_title')}</Text>
          <Text style={s.sub}>{dateLabel}</Text>
          <TouchableOpacity onPress={handleClose} style={s.close} hitSlop={{top:10,right:10,bottom:10,left:10}}>
            <Text style={s.closeTxt}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={{ maxHeight: scrollMaxHeight }} contentContainerStyle={{ padding: 18, paddingTop: 8 }}>
          {/* ─── Étape 1 : choix de la source ─── */}
          {step === 'source' && (
            <>
              {/* Tenue du jour — v1.12.242 : preview + confirme. v1.12.243 : Régénérer */}
              {homeOutfit && (homeOutfit.items?.length > 0) && (
                <TouchableOpacity
                  style={s.sourceCard}
                  activeOpacity={0.85}
                  onPress={() => {
                    // Démarre sur l'option 0 (= homeOutfit principal)
                    const opt = homeOutfitOptions[0] || homeOutfit;
                    setSelectedLook(opt);
                    setTjIndex(0);
                    setStep('tenue_du_jour_confirm');
                  }}>
                  <Text style={s.sourceIcon}>{user?.gender === 'M' ? '🧥' : '👗'}</Text>
                  <View style={{ flex: 1 }}>
                    <Text style={s.sourceTitle}>{t('source_outfit_of_day')}</Text>
                    <Text style={s.sourceDesc}>
                      {homeOutfit.items.length} {lang === 'en' ? (homeOutfit.items.length > 1 ? 'items' : 'item') : `pièce${homeOutfit.items.length > 1 ? 's' : ''}`} — {t('ai_recommendation')}
                      {homeOutfitOptions.length > 1 ? ` · ${homeOutfitOptions.length} ${t('alternatives')}` : ''}
                    </Text>
                  </View>
                  <Text style={s.chev}>›</Text>
                </TouchableOpacity>
              )}

              {/* Mes Looks */}
              <TouchableOpacity
                style={s.sourceCard}
                activeOpacity={0.85}
                disabled={myLooks.length === 0}
                onPress={() => setStep('mes_looks')}>
                <Text style={s.sourceIcon}>✨</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.sourceTitle}>{t('source_my_looks')}</Text>
                  <Text style={s.sourceDesc}>
                    {myLooks.length === 0
                      ? t('no_look_created')
                      : (lang === 'en'
                          ? `${myLooks.length} personalised look${myLooks.length > 1 ? 's' : ''}`
                          : `${myLooks.length} look${myLooks.length > 1 ? 's' : ''} personnalisé${myLooks.length > 1 ? 's' : ''}`)}
                  </Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>

              {/* v1.12.238 — Looks IA générés (v1.12.810 — toujours cliquable, fallback API) */}
              <TouchableOpacity
                style={s.sourceCard}
                activeOpacity={0.85}
                onPress={() => setStep('looks_ia')}>
                <Text style={s.sourceIcon}>🤖</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.sourceTitle}>{t('source_ai_looks')}</Text>
                  <Text style={s.sourceDesc}>
                    {looksIA.length === 0
                      ? t('no_ai_look_generated')
                      : (lang === 'en'
                          ? `${looksIA.length} AI look${looksIA.length > 1 ? 's' : ''} available`
                          : `${looksIA.length} look${looksIA.length > 1 ? 's' : ''} IA disponible${looksIA.length > 1 ? 's' : ''}`)}
                  </Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>

              {/* Manuel photo */}
              <TouchableOpacity
                style={s.sourceCard}
                activeOpacity={0.85}
                onPress={() => setStep('manual')}>
                <Text style={s.sourceIcon}>📸</Text>
                <View style={{ flex: 1 }}>
                  <Text style={s.sourceTitle}>{t('source_manual_photo')}</Text>
                  <Text style={s.sourceDesc}>{t('manual_photo_desc')}</Text>
                </View>
                <Text style={s.chev}>›</Text>
              </TouchableOpacity>
            </>
          )}

          {/* v1.12.250 — Étape 2e : Date picker (mode raccourci depuis Home/Looks/Mes Looks) */}
          {/* v1.12.288 — Mini-calendrier mensuel avec navigation prev/next mois (web-friendly) */}
          {step === 'date_picker' && selectedLook && (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const isSameDay = (a, b) => a.toDateString() === b.toDateString();
            const chipLocale = lang === 'en' ? 'en-US' : 'fr-FR';
            // viewYM = mois affiché (par défaut le mois de la date sélectionnée)
            const baseMonth = new Date(pickedDate.getFullYear(), pickedDate.getMonth(), 1);
            const viewYM = calendarViewYM || baseMonth;
            const year = viewYM.getFullYear();
            const month = viewYM.getMonth();
            // 1er jour du mois → offset (lundi-first : L=0, M=1, M=2, J=3, V=4, S=5, D=6)
            const firstOfMonth = new Date(year, month, 1);
            const jsDow = firstOfMonth.getDay(); // 0=dim, 1=lun, ..., 6=sam
            const leadingBlanks = (jsDow + 6) % 7; // convert to lundi-first
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const monthLabel = firstOfMonth.toLocaleDateString(chipLocale, { month: 'long', year: 'numeric' });
            const dayHeaders = lang === 'en' ? ['M','T','W','T','F','S','S'] : ['L','M','M','J','V','S','D'];
            // Build cells array (7 cols × N rows, blanks + day numbers)
            const cells = [];
            for (let i = 0; i < leadingBlanks; i++) cells.push(null);
            for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
            // Pad to fill last row
            while (cells.length % 7 !== 0) cells.push(null);
            const goPrevMonth = () => setCalendarViewYM(new Date(year, month - 1, 1));
            const goNextMonth = () => setCalendarViewYM(new Date(year, month + 1, 1));
            return (
              <>
                <Text style={s.sectionLabel}>{t('for_which_day')}</Text>
                <Text style={{ fontSize: 12, color: '#7A7268', marginBottom: 10 }}>
                  {t('select_date_worn')}
                </Text>

                {/* Header mensuel : ‹ Mois Année › */}
                <View style={s.monthNavRow}>
                  <TouchableOpacity onPress={goPrevMonth} style={s.monthNavBtn} activeOpacity={0.7}>
                    <Text style={s.monthNavChev}>‹</Text>
                  </TouchableOpacity>
                  <Text style={s.monthNavLabel}>{monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)}</Text>
                  <TouchableOpacity onPress={goNextMonth} style={s.monthNavBtn} activeOpacity={0.7}>
                    <Text style={s.monthNavChev}>›</Text>
                  </TouchableOpacity>
                </View>

                {/* Day headers L M M J V S D */}
                <View style={s.calRow}>
                  {dayHeaders.map((h, i) => (
                    <View key={i} style={s.calCell}>
                      <Text style={s.calDayHeader}>{h}</Text>
                    </View>
                  ))}
                </View>

                {/* Grille des jours */}
                {Array.from({ length: cells.length / 7 }).map((_, rowIdx) => (
                  <View key={rowIdx} style={s.calRow}>
                    {cells.slice(rowIdx * 7, rowIdx * 7 + 7).map((cell, colIdx) => {
                      if (!cell) return <View key={colIdx} style={s.calCell} />;
                      const active = isSameDay(cell, pickedDate);
                      const isToday = isSameDay(cell, today);
                      const isPast = cell < today;
                      return (
                        <TouchableOpacity
                          key={colIdx}
                          style={[s.calCell, s.calDay,
                            active && s.calDayActive,
                            !active && isToday && s.calDayToday,
                          ]}
                          activeOpacity={0.7}
                          onPress={() => setPickedDate(new Date(cell))}
                        >
                          <Text style={[s.calDayText,
                            active && s.calDayTextActive,
                            !active && isToday && s.calDayTextToday,
                            !active && !isToday && isPast && s.calDayTextPast,
                          ]}>
                            {cell.getDate()}
                          </Text>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                ))}

                {/* Aperçu de la tenue choisie */}
                <Text style={s.sectionLabel}>{t('outfit_to_add')}</Text>
                <View style={s.confirmBox}>
                  <Text style={s.confirmTitle}>{selectedLook.name || t('selected_outfit')}</Text>
                  <Text style={s.confirmCount}>
                    {(selectedLook.items || []).length} {lang === 'en' ? ((selectedLook.items || []).length > 1 ? 'items' : 'item') : `pièce${(selectedLook.items || []).length > 1 ? 's' : ''}`}
                    {preselectedSource ? ` · ${preselectedSource === 'tenue_du_jour' ? t('source_outfit_of_day') : preselectedSource === 'mes_looks' ? t('source_my_looks') : t('source_ai_looks')}` : ''}
                  </Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingTop: 8 }}>
                    {(selectedLook.items || []).slice(0, 6).map((it, i) => (
                      <View key={i} style={s.confirmThumb}>
                        {(it.image_url || it.image)
                          ? <Image source={{ uri: it.image_url || it.image }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                          : <Text style={{ fontSize: 22 }}>👗</Text>}
                        {it.category && (
                          <Text style={s.confirmThumbCat} numberOfLines={1}>{it.category}</Text>
                        )}
                      </View>
                    ))}
                  </ScrollView>
                </View>

                {/* v1.12.740 — Sélecteur "Photo principale" si flat-lay/try-on existent */}
                {lookResults && (lookResults.flatlay || lookResults.tryon) ? (
                  <>
                    <Text style={[s.sectionLabel, { marginTop: 14 }]}>{lang === 'en' ? 'Cover photo' : 'Photo principale'}</Text>
                    <Text style={{ fontSize: 11, color: '#7A7268', marginBottom: 8 }}>
                      {lang === 'en' ? 'Choose what appears as the main image on the calendar' : 'Choisissez ce qui apparaîtra comme image principale dans le calendrier'}
                    </Text>
                    <View style={{ flexDirection: 'row', gap: 8 }}>
                      {/* Articles (default) */}
                      <TouchableOpacity
                        onPress={() => setPhotoSource('articles')}
                        activeOpacity={0.8}
                        style={[
                          { flex: 1, borderRadius: 12, overflow: 'hidden', borderWidth: 2, backgroundColor: '#FAFAFA' },
                          { borderColor: photoSource === 'articles' ? GOLD : BORDER },
                        ]}>
                        <View style={{ height: 80, alignItems: 'center', justifyContent: 'center' }}>
                          <Text style={{ fontSize: 28 }}>🧩</Text>
                        </View>
                        <Text style={{ textAlign: 'center', fontSize: 11, fontWeight: '600', color: photoSource === 'articles' ? GOLD : '#5F584F', paddingVertical: 6 }}>
                          {lang === 'en' ? 'Items' : 'Articles'}
                        </Text>
                      </TouchableOpacity>
                      {/* Flat-lay si dispo */}
                      {lookResults.flatlay ? (
                        <TouchableOpacity
                          onPress={() => setPhotoSource('flatlay')}
                          activeOpacity={0.8}
                          style={[
                            { flex: 1, borderRadius: 12, overflow: 'hidden', borderWidth: 2, backgroundColor: '#F5F1EA' },
                            { borderColor: photoSource === 'flatlay' ? GOLD : BORDER },
                          ]}>
                          <Image source={{ uri: lookResults.flatlay }} style={{ width: '100%', height: 80 }} resizeMode="cover" />
                          <Text style={{ textAlign: 'center', fontSize: 11, fontWeight: '600', color: photoSource === 'flatlay' ? GOLD : '#5F584F', paddingVertical: 6 }}>
                            🎨 Flat-lay
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                      {/* Try-on si dispo */}
                      {lookResults.tryon ? (
                        <TouchableOpacity
                          onPress={() => setPhotoSource('tryon')}
                          activeOpacity={0.8}
                          style={[
                            { flex: 1, borderRadius: 12, overflow: 'hidden', borderWidth: 2, backgroundColor: '#F5F1EA' },
                            { borderColor: photoSource === 'tryon' ? GOLD : BORDER },
                          ]}>
                          <Image source={{ uri: lookResults.tryon }} style={{ width: '100%', height: 80 }} resizeMode="cover" />
                          <Text style={{ textAlign: 'center', fontSize: 11, fontWeight: '600', color: photoSource === 'tryon' ? GOLD : '#5F584F', paddingVertical: 6 }}>
                            ✨ {lang === 'en' ? 'Try-on' : 'Essai'}
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                    </View>
                  </>
                ) : null}
              </>
            );
          })()}

          {/* v1.12.242+243 — Étape 2d : Confirmation Tenue du jour (preview + régénérer + ajouter) */}
          {step === 'tenue_du_jour_confirm' && selectedLook && (
            <>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <TouchableOpacity onPress={() => { setSelectedLook(null); setStep('source'); }} style={s.back}>
                  <Text style={s.backTxt}>{t('back_arrow')}</Text>
                </TouchableOpacity>
                {/* v1.12.243 — Régénérer : cycle dans homeOutfitOptions */}
                {homeOutfitOptions.length > 1 && (
                  <TouchableOpacity
                    style={s.regenBtn}
                    activeOpacity={0.8}
                    onPress={() => {
                      const next = (tjIndex + 1) % homeOutfitOptions.length;
                      setTjIndex(next);
                      setSelectedLook(homeOutfitOptions[next] || homeOutfit);
                    }}>
                    <Text style={s.regenTxt}>{t('regenerate_arrow')} ({tjIndex + 1}/{homeOutfitOptions.length})</Text>
                  </TouchableOpacity>
                )}
              </View>
              <Text style={s.sectionLabel}>{t('outfit_of_day_proposed')}</Text>
              <View style={s.confirmBox}>
                <Text style={s.confirmTitle}>
                  {selectedLook.name || t('ai_recommendation_of_day')}
                </Text>
                {selectedLook.occasion && (
                  <Text style={s.confirmOcc}>{t('for')} {selectedLook.occasion}</Text>
                )}
                <Text style={s.confirmCount}>
                  {(selectedLook.items || []).length} {lang === 'en' ? ((selectedLook.items || []).length > 1 ? 'items' : 'item') : `pièce${(selectedLook.items || []).length > 1 ? 's' : ''}`}
                </Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingTop: 8 }}>
                  {(selectedLook.items || []).slice(0, 6).map((it, i) => (
                    <View key={i} style={s.confirmThumb}>
                      {(it.image_url || it.image)
                        ? <Image source={{ uri: it.image_url || it.image }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                        : <Text style={{ fontSize: 22 }}>👗</Text>}
                      {it.category && (
                        <Text style={s.confirmThumbCat} numberOfLines={1}>{it.category}</Text>
                      )}
                    </View>
                  ))}
                </ScrollView>
                <Text style={s.confirmAsk}>
                  {lang === 'en' ? `Would you like to add it on ${dateLabel}?` : `Voulez-vous l'ajouter au ${dateLabel} ?`}
                </Text>
              </View>
            </>
          )}

          {/* v1.12.238+242 — Étape 2c : Looks IA avec recherche + filtre catégorie */}
          {step === 'looks_ia' && (
            <>
              <TouchableOpacity onPress={() => { setSelectedLook(null); setStep('source'); }} style={s.back}>
                <Text style={s.backTxt}>{t('back_arrow')}</Text>
              </TouchableOpacity>
              <Text style={s.sectionLabel}>
                {t('source_ai_looks')} ({filteredLooksIA.length}{filteredLooksIA.length !== looksIA.length ? `/${looksIA.length}` : ''})
              </Text>
              {looksIA.length === 0 ? (
                <Text style={s.emptyTxt}>{t('no_ai_look_yet_hint')}</Text>
              ) : (
                <>
                  {/* Search bar (nom + marque + pseudo) */}
                  <View style={s.searchBar}>
                    <Text style={s.searchIcon}>🔍</Text>
                    <TextInput
                      value={iaSearch}
                      onChangeText={setIaSearch}
                      placeholder={t('search_placeholder_full')}
                      placeholderTextColor="#A89E94"
                      style={s.searchInput}
                      returnKeyType="search"
                    />
                    {iaSearch.length > 0 && (
                      <TouchableOpacity onPress={() => setIaSearch('')} hitSlop={{top:8,right:8,bottom:8,left:8}}>
                        <Text style={s.searchClear}>✕</Text>
                      </TouchableOpacity>
                    )}
                  </View>

                  {/* v1.12.246 — Filtre par article (photo dressing) */}
                  {wardrobe.length > 0 && (
                    <TouchableOpacity
                      style={s.articleFilterBtn}
                      activeOpacity={0.85}
                      onPress={() => setIaShowArticleFilter(v => !v)}>
                      <Text style={s.articleFilterTxt}>
                        🖼  {t('filter_by_item')} {iaArticleIds.size > 0 ? `(${iaArticleIds.size} ${lang === 'en' ? (iaArticleIds.size > 1 ? 'selected' : 'selected') : `sélectionné${iaArticleIds.size > 1 ? 's' : ''}`})` : ''} {iaShowArticleFilter ? '▲' : '▼'}
                      </Text>
                    </TouchableOpacity>
                  )}
                  {iaShowArticleFilter && wardrobe.length > 0 && (
                    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.articleFilterRow}>
                      {wardrobe.slice(0, 80).map(w => {
                        const active = iaArticleIds.has(String(w.id));
                        return (
                          <TouchableOpacity
                            key={w.id}
                            activeOpacity={0.8}
                            style={[s.articleThumb, active && s.articleThumbActive]}
                            onPress={() => {
                              setIaArticleIds(prev => {
                                const next = new Set(prev);
                                if (next.has(String(w.id))) next.delete(String(w.id));
                                else next.add(String(w.id));
                                return next;
                              });
                            }}>
                            {(w.image_url || w.image)
                              ? <Image source={{ uri: w.image_url || w.image }} style={{ width: '100%', height: '100%' }} resizeMode="cover" />
                              : <Text style={{ fontSize: 18 }}>👗</Text>}
                            {active && (
                              <View style={s.articleCheck}>
                                <Text style={{ fontSize: 10, color: '#FFFFFF', fontWeight: '700' }}>✓</Text>
                              </View>
                            )}
                            {(w.ai_metadata?.pseudo || w.name) && (
                              <Text style={s.articleThumbLabel} numberOfLines={1}>
                                {w.ai_metadata?.pseudo || (w.name || '').slice(0, 14)}
                              </Text>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                      {iaArticleIds.size > 0 && (
                        <TouchableOpacity
                          style={s.articleClearBtn}
                          activeOpacity={0.8}
                          onPress={() => setIaArticleIds(new Set())}>
                          <Text style={s.articleClearTxt}>{t('clear_filter')}</Text>
                        </TouchableOpacity>
                      )}
                    </ScrollView>
                  )}

                  {/* Category chips */}
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={s.iaCatRow}>
                    {iaAvailableCats.map(cat => {
                      const active = iaCatFilter === cat;
                      return (
                        <TouchableOpacity
                          key={cat}
                          onPress={() => setIaCatFilter(cat)}
                          style={[s.iaCatChip, active && s.iaCatChipActive]}
                          activeOpacity={0.8}>
                          <Text style={[s.iaCatChipTxt, active && s.iaCatChipTxtActive]}>{
                            cat === 'Tout' ? t('all')
                              : (lang === 'en'
                                  ? ({ 'Bureau': 'Office', 'Soirée': 'Evening', 'Romantique': 'Romantic', 'Minimaliste': 'Minimal', 'Bohème': 'Bohemian' }[cat] || cat)
                                  : cat)
                          }</Text>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>

                  {filteredLooksIA.length === 0 ? (
                    <Text style={s.emptyTxt}>{t('no_look_matches_search')}</Text>
                  ) : (
                    <>
                      {/* v1.12.811 — Cards plein largeur avec grid items 80x100 */}
                      {filteredLooksIA.slice(0, iaVisibleCount).map((l, idx) => {
                        const isSel = (selectedLook === l) || (l.id && selectedLook?.id === l.id);
                        const items = (l.items || []).slice(0, 4);
                        return (
                          <TouchableOpacity
                            key={l.id ? `ia_${l.id}` : `ia_idx_${idx}`}
                            style={[s.iaLookCard, isSel && s.iaLookCardActive]}
                            activeOpacity={0.85}
                            onPress={() => {
                              console.log('[CalendarModal] AI look selected:', { id: l.id, name: l.name, items: items.length });
                              setSelectedLook(l);
                            }}>
                            {/* Header: nom + occasion */}
                            <View style={{ paddingHorizontal: 12, paddingTop: 10, paddingBottom: 6 }}>
                              <Text style={s.iaLookName} numberOfLines={1}>{l.name || `Look IA #${idx + 1}`}</Text>
                              <Text style={s.iaLookSub}>
                                {(l.occasion || 'IA').toUpperCase()} · {(l.items || []).length} {t('pieces')}
                              </Text>
                            </View>
                            {/* Grid d'images 4 colonnes — v1.12.812 LazyImage + safe https */}
                            <View style={s.iaThumbsGrid}>
                              {items.map((it, i) => {
                                const uri = safeImgUri(it.image_url || it.image);
                                if (idx === 0 && i === 0) console.log(`[CalendarModal] first item img:`, { id: it.id, image_url: it.image_url, normalized: uri });
                                return (
                                  <View key={`${it.id || i}-${i}`} style={s.iaThumb}>
                                    <LazyImage
                                      uri={uri}
                                      style={{ width: '100%', height: '100%' }}
                                      resizeMode="cover"
                                      fallbackEmoji="👗"
                                    />
                                    {it.category && (
                                      <View style={s.iaThumbCatBadge}>
                                        <Text style={s.iaThumbCatTxt} numberOfLines={1}>{it.category}</Text>
                                      </View>
                                    )}
                                  </View>
                                );
                              })}
                              {items.length < 4 && Array.from({ length: 4 - items.length }).map((_, i) => (
                                <View key={`empty-${i}`} style={[s.iaThumb, { backgroundColor: '#F9F5EE' }]} />
                              ))}
                            </View>
                            {/* Checkmark si sélectionné */}
                            {isSel && (
                              <View style={s.iaSelectedBadge}>
                                <Text style={{ fontSize: 14, color: '#FFFFFF', fontWeight: '800' }}>✓</Text>
                              </View>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                      {/* v1.12.249 — Pagination : "Charger plus" si plus de looks que visibleCount */}
                      {filteredLooksIA.length > iaVisibleCount && (
                        <TouchableOpacity
                          style={s.loadMoreBtn}
                          activeOpacity={0.85}
                          onPress={() => setIaVisibleCount(c => c + PAGE_SIZE)}>
                          <Text style={s.loadMoreTxt}>
                            {lang === 'en'
                              ? `↓ Load more (${filteredLooksIA.length - iaVisibleCount} remaining)`
                              : `↓ Charger plus (${filteredLooksIA.length - iaVisibleCount} restants)`}
                          </Text>
                        </TouchableOpacity>
                      )}
                      {/* Total visible counter */}
                      <Text style={s.paginationInfo}>
                        {Math.min(iaVisibleCount, filteredLooksIA.length)} / {filteredLooksIA.length} {t('displayed')}
                      </Text>
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ─── Étape 2a : Mes Looks ─── */}
          {step === 'mes_looks' && (
            <>
              <TouchableOpacity onPress={() => setStep('source')} style={s.back}>
                <Text style={s.backTxt}>{t('back_arrow')}</Text>
              </TouchableOpacity>
              <Text style={s.sectionLabel}>{t('select_a_look')}</Text>
              {myLooks.length === 0 ? (
                <Text style={s.emptyTxt}>{t('no_personalised_look')}</Text>
              ) : (
                myLooks.map(l => (
                  <TouchableOpacity
                    key={l.id}
                    style={[s.lookRow, selectedLook?.id === l.id && s.lookRowActive]}
                    activeOpacity={0.85}
                    onPress={() => setSelectedLook(l)}>
                    <View style={s.thumbsRow}>
                      {(l.items || []).slice(0, 3).map((it, i) => (
                        <View key={i} style={s.thumb}>
                          {it.image_url || it.image
                            ? <Image source={{ uri: it.image_url || it.image }} style={{ width: '100%', height: '100%' }} />
                            : <Text style={{ fontSize: 18 }}>👗</Text>}
                        </View>
                      ))}
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={s.lookName} numberOfLines={1}>{l.name || `Look #${String(l.id).slice(-4)}`}</Text>
                      <Text style={s.lookSub}>{(l.occasion || (lang === 'en' ? 'no occasion' : 'sans occasion')).toUpperCase()} · {(l.items || []).length} {t('pieces')}</Text>
                    </View>
                  </TouchableOpacity>
                ))
              )}
            </>
          )}

          {/* ─── Étape 2b : Manuel photo ─── */}
          {step === 'manual' && (
            <>
              <TouchableOpacity onPress={() => setStep('source')} style={s.back}>
                <Text style={s.backTxt}>{t('back_arrow')}</Text>
              </TouchableOpacity>
              <Text style={s.sectionLabel}>{t('outfit_photo_optional')}</Text>
              <TouchableOpacity style={s.photoBox} activeOpacity={0.85} onPress={pickPhoto}>
                {manualPhoto
                  ? <Image source={{ uri: manualPhoto }} style={{ width: '100%', height: '100%', borderRadius: 12 }} resizeMode="contain" />
                  : <View style={{ alignItems: 'center', gap: 6 }}>
                      <Text style={{ fontSize: 32 }}>📷</Text>
                      <Text style={{ fontSize: 12, color: '#7A7268' }}>{t('tap_to_import')}</Text>
                    </View>}
              </TouchableOpacity>

              <Text style={s.sectionLabel}>{t('occasion')}</Text>
              <View style={s.chipsRow}>
                {CALENDAR_OCCASIONS.map(o => (
                  <TouchableOpacity
                    key={o.id}
                    style={[s.chip, occasion === o.id && s.chipActive]}
                    activeOpacity={0.85}
                    onPress={() => setOccasion(occasion === o.id ? null : o.id)}>
                    <Text style={[s.chipTxt, occasion === o.id && s.chipTxtActive]}>{o.emoji} {o.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={s.sectionLabel}>{t('notes')}</Text>
              <TextInput
                value={notes}
                onChangeText={setNotes}
                placeholder={t('notes_placeholder')}
                placeholderTextColor="#A89E94"
                multiline
                style={s.notesInput}
                maxLength={300}
              />

            </>
          )}

          {step === 'submitting' && (
            <View style={{ alignItems: 'center', padding: 32, gap: 12 }}>
              <ActivityIndicator size="small" color={GOLD} />
              <Text style={{ fontSize: 13, color: '#7A7268' }}>{t('saving_ellipsis')}</Text>
            </View>
          )}
        </ScrollView>

        {/* v1.12.236 — Footer CTA fixe (hors ScrollView) → bouton toujours visible */}
        {(step === 'mes_looks' && selectedLook) && (
          <View style={s.footer}>
            <TouchableOpacity
              style={s.cta}
              activeOpacity={0.9}
              onPress={() => submit({ source: 'mes_looks', look: selectedLook })}>
              <Text style={s.ctaTxt}>{t('save_this_look')}</Text>
            </TouchableOpacity>
          </View>
        )}
        {(step === 'looks_ia' && selectedLook) && (
          <View style={s.footer}>
            <TouchableOpacity
              style={s.cta}
              activeOpacity={0.9}
              onPress={() => submit({ source: 'looks_ia', look: selectedLook })}>
              <Text style={s.ctaTxt}>{t('save_this_ai_look')}</Text>
            </TouchableOpacity>
          </View>
        )}
        {step === 'tenue_du_jour_confirm' && selectedLook && (
          <View style={s.footer}>
            <TouchableOpacity
              style={s.cta}
              activeOpacity={0.9}
              onPress={() => submit({ source: 'tenue_du_jour', look: selectedLook })}>
              <Text style={s.ctaTxt}>{t('confirm_and_add')}</Text>
            </TouchableOpacity>
          </View>
        )}
        {/* v1.12.250 — Footer date_picker (mode raccourci) */}
        {step === 'date_picker' && selectedLook && (
          <View style={s.footer}>
            <TouchableOpacity
              style={s.cta}
              activeOpacity={0.9}
              onPress={() => submit({
                source: preselectedSource || 'mes_looks',
                look: selectedLook,
                // v1.12.740 — Photo principale choisie : flat-lay ou try-on si sélectionné
                photoUri: photoSource === 'flatlay' ? lookResults?.flatlay
                       : photoSource === 'tryon'   ? lookResults?.tryon
                       : null,
              })}>
              <Text style={s.ctaTxt}>{lang === 'en' ? `Confirm for ${dateLabel}` : `Confirmer pour ${dateLabel}`}</Text>
            </TouchableOpacity>
          </View>
        )}
        {step === 'manual' && (
          <View style={s.footer}>
            <TouchableOpacity
              style={[s.cta, !manualPhoto && !occasion && !notes.trim() && s.ctaDisabled]}
              activeOpacity={0.9}
              onPress={() => submit({ source: 'manual', photoUri: manualPhoto })}>
              <Text style={s.ctaTxt}>{t('save')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </AppModal>
  );
}

const s = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderRadius: 18,
    overflow: 'hidden',
    // v1.13.41 — bordure dorée autour du popup "Ajouter une tenue" / "Add an outfit"
    borderWidth: 1.5,
    borderColor: GOLD,
  },
  header: {
    paddingTop: 18, paddingHorizontal: 18, paddingBottom: 6,
    borderBottomWidth: 1, borderBottomColor: BORDER,
    position: 'relative',
  },
  title:    { fontSize: 16, fontWeight: '700', color: '#1A1814', letterSpacing: 0.2 },
  sub:      { fontSize: 12, color: '#7A7268', marginTop: 2 },
  close:    { position: 'absolute', top: 14, right: 14, width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  closeTxt: { fontSize: 18, color: '#7A7268' },

  sourceCard: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingVertical: 14, paddingHorizontal: 14,
    borderWidth: 1, borderColor: BORDER, borderRadius: 14,
    marginBottom: 10,
    backgroundColor: '#FFFFFF',
  },
  sourceIcon:  { fontSize: 28 },
  sourceTitle: { fontSize: 14, fontWeight: '700', color: '#1A1814' },
  sourceDesc:  { fontSize: 12, color: '#7A7268', marginTop: 2 },
  chev:        { fontSize: 22, color: '#A89E94' },

  back:    { paddingVertical: 6, marginBottom: 8 },
  backTxt: { fontSize: 13, color: GOLD, fontWeight: '600' },

  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#1A1814', letterSpacing: 0.4, textTransform: 'uppercase', marginTop: 12, marginBottom: 8 },

  lookRow: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    padding: 10,
    borderWidth: 1, borderColor: BORDER, borderRadius: 12,
    marginBottom: 8,
  },
  lookRowActive: { borderColor: GOLD, backgroundColor: '#FFF7E0' },
  thumbsRow: { flexDirection: 'row', gap: 4 },
  thumb: {
    width: 38, height: 46, borderRadius: 6, overflow: 'hidden',
    backgroundColor: '#F5F2ED', alignItems: 'center', justifyContent: 'center',
  },
  lookName: { fontSize: 13, fontWeight: '600', color: '#1A1814' },
  lookSub:  { fontSize: 10, color: '#7A7268', marginTop: 2, letterSpacing: 0.4 },

  // v1.12.811 — Card pleine largeur pour Looks IA avec grille images
  iaLookCard: {
    borderWidth: 1, borderColor: BORDER, borderRadius: 14,
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  iaLookCardActive: { borderColor: GOLD, borderWidth: 2, backgroundColor: '#FFFCF5' },
  iaLookName: { fontSize: 14, fontWeight: '700', color: '#1A1814', letterSpacing: 0.2 },
  iaLookSub: { fontSize: 10, color: '#7A7268', marginTop: 2, letterSpacing: 0.5, fontWeight: '600' },
  iaThumbsGrid: {
    flexDirection: 'row',
    paddingHorizontal: 8,
    paddingBottom: 10,
    gap: 6,
  },
  iaThumb: {
    flex: 1,
    height: 110, // v1.12.812 — height fixe au lieu d'aspectRatio (web RN aspectRatio+flex parfois broken)
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#F5F2ED',
    position: 'relative',
  },
  iaThumbCatBadge: {
    position: 'absolute', bottom: 4, left: 4, right: 4,
    backgroundColor: 'rgba(26,24,20,0.78)',
    paddingHorizontal: 6, paddingVertical: 2,
    borderRadius: 4, alignItems: 'center',
  },
  iaThumbCatTxt: { fontSize: 9, color: '#FFF', fontWeight: '600', letterSpacing: 0.3 },
  iaSelectedBadge: {
    position: 'absolute', top: 10, right: 10,
    width: 28, height: 28, borderRadius: 14,
    backgroundColor: GOLD,
    alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOpacity: 0.18, shadowRadius: 4, shadowOffset: { width: 0, height: 2 },
    elevation: 3,
  },
  emptyTxt: { fontSize: 12, color: '#A89E94', fontStyle: 'italic', textAlign: 'center', padding: 20 },

  photoBox: {
    // v1.12.235 — height augmentée + aspectRatio 4:5 pour photos full body sans crop
    width: '100%', aspectRatio: 4 / 5, maxHeight: 360,
    borderRadius: 14, borderWidth: 1.5, borderColor: BORDER, borderStyle: 'dashed',
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#FBF7EE',
    overflow: 'hidden',
  },
  chipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: {
    paddingHorizontal: 10, paddingVertical: 6,
    borderRadius: 14,
    borderWidth: 1, borderColor: BORDER,
    backgroundColor: '#FFFFFF',
  },
  chipActive: { borderColor: GOLD, backgroundColor: '#FFF7E0' },
  chipTxt:    { fontSize: 11, color: '#7A7268' },
  chipTxtActive: { color: '#B8860B', fontWeight: '700' },
  notesInput: {
    borderWidth: 1, borderColor: BORDER, borderRadius: 10,
    padding: 10, fontSize: 13, color: '#1A1814',
    minHeight: 60, textAlignVertical: 'top',
  },

  // v1.12.243 — Bouton Régénérer
  regenBtn: {
    paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: '#FFF7E0',
    borderWidth: 1, borderColor: GOLD,
  },
  regenTxt: { fontSize: 11, color: '#B8860B', fontWeight: '700', letterSpacing: 0.4 },

  // v1.12.242 — Confirmation Tenue du jour
  confirmBox: {
    padding: 14,
    backgroundColor: '#FBF7EE',
    borderWidth: 1, borderColor: 'rgba(212,175,114,0.35)',
    borderRadius: 14, gap: 4,
  },
  confirmTitle: { fontSize: 14, fontWeight: '700', color: '#1A1814' },
  confirmOcc:   { fontSize: 11, color: '#9B7B5A', fontStyle: 'italic' },
  confirmCount: { fontSize: 11, color: '#7A7268', letterSpacing: 0.4, textTransform: 'uppercase' },
  confirmThumb: {
    width: 70, height: 88, borderRadius: 8,
    backgroundColor: '#FFFFFF', overflow: 'hidden',
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: BORDER,
    position: 'relative',
  },
  confirmThumbCat: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    color: '#FFD700', fontSize: 8, fontWeight: '700',
    textAlign: 'center', paddingVertical: 2, letterSpacing: 0.4,
  },
  confirmAsk: { marginTop: 10, fontSize: 13, color: '#1A1814', fontWeight: '500' },

  // v1.12.242 — Search + cat chips Looks IA
  searchBar: {
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1, borderColor: BORDER,
    paddingHorizontal: 12, height: 40,
    marginTop: 4, marginBottom: 8,
    gap: 8,
  },
  searchIcon: { fontSize: 13, opacity: 0.6 },
  searchInput: {
    flex: 1, fontSize: 13, color: '#1A1814',
    paddingVertical: 0,
  },
  searchClear: { fontSize: 13, color: '#A89E94', paddingHorizontal: 4 },
  iaCatRow: { gap: 6, paddingBottom: 8, paddingRight: 8 },
  iaCatChip: {
    paddingHorizontal: 12, paddingVertical: 6,
    borderRadius: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: BORDER,
  },
  iaCatChipActive: { backgroundColor: '#FFF7E0', borderColor: GOLD },
  iaCatChipTxt: { fontSize: 11, color: '#7A7268', fontWeight: '500' },
  iaCatChipTxtActive: { color: '#B8860B', fontWeight: '700' },

  // v1.12.246 — Filtre par article (toggle + thumbs wardrobe)
  articleFilterBtn: {
    paddingVertical: 8, paddingHorizontal: 12,
    backgroundColor: '#FBF7EE',
    borderRadius: 10,
    borderWidth: 1, borderColor: 'rgba(212,175,114,0.4)',
    marginBottom: 8,
  },
  articleFilterTxt: { fontSize: 12, color: '#1A1814', fontWeight: '600' },
  articleFilterRow: { gap: 6, paddingBottom: 8, paddingRight: 4 },
  articleThumb: {
    width: 60, height: 76,
    borderRadius: 8, overflow: 'hidden',
    backgroundColor: '#FBF7EE',
    borderWidth: 1, borderColor: BORDER,
    alignItems: 'center', justifyContent: 'center',
    position: 'relative',
  },
  articleThumbActive: { borderWidth: 2, borderColor: GOLD },
  articleCheck: {
    position: 'absolute', top: 3, right: 3,
    width: 16, height: 16, borderRadius: 8,
    backgroundColor: GOLD,
    alignItems: 'center', justifyContent: 'center',
  },
  articleThumbLabel: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    color: '#FFFFFF', fontSize: 8,
    paddingVertical: 2, textAlign: 'center', letterSpacing: 0.2,
  },
  articleClearBtn: {
    paddingHorizontal: 12,
    alignItems: 'center', justifyContent: 'center',
    borderRadius: 8,
    backgroundColor: '#1A1814',
    height: 76,
  },
  articleClearTxt: { fontSize: 11, color: '#FFFFFF', fontWeight: '700' },

  // v1.12.250 — Date picker chips
  dateChip: {
    minWidth: 56, paddingVertical: 8, paddingHorizontal: 8,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: BORDER,
    alignItems: 'center', position: 'relative',
  },
  dateChipActive: { backgroundColor: '#1A1814', borderColor: '#1A1814' },
  dateChipToday:  { borderColor: GOLD },
  dateChipDay:    { fontSize: 9, color: '#7A7268', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: '700' },
  dateChipDayActive: { color: GOLD },
  dateChipNum:    { fontSize: 18, fontWeight: '700', color: '#1A1814', marginVertical: 2 },
  dateChipNumActive: { color: '#FFFFFF' },
  dateChipMonth:  { fontSize: 8, color: '#A89E94', textTransform: 'uppercase', letterSpacing: 0.3 },
  dateChipMonthActive: { color: '#D4AF72' },
  dateChipDot: {
    position: 'absolute', bottom: 4, alignSelf: 'center',
    width: 4, height: 4, borderRadius: 2, backgroundColor: GOLD,
  },

  // v1.12.288 — Mini-calendrier mensuel (web-friendly + mobile)
  monthNavRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: '#FAF8F4',
    borderRadius: 10,
    paddingHorizontal: 8, paddingVertical: 6,
    marginBottom: 10,
  },
  monthNavBtn: {
    width: 36, height: 36, borderRadius: 18,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#EAE6E0',
  },
  monthNavChev: { fontSize: 22, color: '#1A1814', lineHeight: 22, fontWeight: '700' },
  monthNavLabel: { fontSize: 14, fontWeight: '700', color: '#1A1814', letterSpacing: 0.3 },
  calRow: { flexDirection: 'row', width: '100%' },
  calCell: {
    flex: 1, aspectRatio: 1,
    alignItems: 'center', justifyContent: 'center',
    margin: 2,
  },
  calDayHeader: { fontSize: 10, fontWeight: '700', color: '#A89E94', letterSpacing: 0.5 },
  calDay: {
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#EAE6E0',
  },
  calDayToday: { borderColor: GOLD, borderWidth: 1.5 },
  calDayActive: { backgroundColor: '#1A1814', borderColor: '#1A1814' },
  calDayText: { fontSize: 14, fontWeight: '600', color: '#1A1814' },
  calDayTextToday: { color: '#B8860B', fontWeight: '700' },
  calDayTextActive: { color: '#FFFFFF', fontWeight: '700' },
  calDayTextPast: { color: '#C5C0BB' },

  // v1.12.249 — Pagination Looks IA
  loadMoreBtn: {
    marginTop: 8, paddingVertical: 12, paddingHorizontal: 16,
    backgroundColor: '#FBF7EE',
    borderWidth: 1, borderColor: 'rgba(212,175,114,0.45)',
    borderRadius: 12, alignItems: 'center',
  },
  loadMoreTxt: { fontSize: 12, color: '#B8860B', fontWeight: '700', letterSpacing: 0.4 },
  paginationInfo: {
    marginTop: 6, fontSize: 10, color: '#A89E94', textAlign: 'center', fontStyle: 'italic',
  },

  cta: {
    backgroundColor: '#1A1814',
    paddingVertical: 14, borderRadius: 100,
    alignItems: 'center',
  },
  ctaDisabled: { opacity: 0.55 },
  ctaTxt: { fontSize: 12, fontWeight: '700', color: '#FFFFFF', letterSpacing: 1.2 },
  // v1.12.236 — footer fixe en bas de la card (hors ScrollView)
  footer: {
    paddingHorizontal: 18, paddingTop: 12, paddingBottom: 18,
    borderTopWidth: 1, borderTopColor: BORDER,
    backgroundColor: '#FFFFFF',
  },
});
